import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Search, X, Package, ArrowRight } from "lucide-react";
import { api } from "@/lib/api";
import { useTranslation } from "react-i18next";

/**
 * Inline navbar search — visually identical to the homepage Quick Search form
 * (border-2 black, brand-colored shadow, primary submit button) but sized to
 * fit inside the navbar. Shows a live-suggestions dropdown below.
 *
 * Props:
 *   compact: bool  — when true renders a shorter variant for desktop navbar.
 *                     Otherwise the input is larger (used in mobile menu).
 */
export default function NavbarSearch({ compact = true }) {
    const { t } = useTranslation();
    const nav = useNavigate();
    const inputRef = useRef(null);
    const wrapRef = useRef(null);
    const [q, setQ] = useState("");
    const [suggestions, setSuggestions] = useState(null);
    const [open, setOpen] = useState(false);

    // "/" anywhere on the page focuses this input (skip when typing elsewhere)
    useEffect(() => {
        const onKey = (e) => {
            if (e.key !== "/") return;
            const tag = (e.target?.tagName || "").toLowerCase();
            if (["input", "textarea", "select"].includes(tag) || e.target?.isContentEditable) return;
            e.preventDefault();
            inputRef.current?.focus();
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, []);

    // debounce and fetch suggestions
    useEffect(() => {
        const s = q.trim();
        if (s.length < 2) {
            setSuggestions(null);
            return;
        }
        const handle = setTimeout(async () => {
            try {
                const { data } = await api.get("/products", {
                    params: { q: s, per_page: 8, page: 1 },
                });
                setSuggestions(data);
            } catch {
                setSuggestions([]);
            }
        }, 250);
        return () => clearTimeout(handle);
    }, [q]);

    // close dropdown on outside click
    useEffect(() => {
        const onDocClick = (e) => {
            if (!wrapRef.current) return;
            if (!wrapRef.current.contains(e.target)) setOpen(false);
        };
        document.addEventListener("mousedown", onDocClick);
        return () => document.removeEventListener("mousedown", onDocClick);
    }, []);

    const submit = (val) => {
        const target = (val ?? q).trim();
        if (!target) return;
        nav(`/catalog?q=${encodeURIComponent(target)}`);
        setOpen(false);
    };

    const inputPad = compact ? "py-2.5" : "py-3";
    const iconMargin = compact ? "ml-4" : "ml-4";
    const submitPad = compact ? "pl-5 pr-4 py-2 text-xs" : "pl-5 pr-4 py-2.5 text-sm";

    return (
        <div
            ref={wrapRef}
            className={`relative ${compact ? "hidden md:flex flex-1 max-w-xl mx-auto lg:ml-12" : "w-full"}`}
            data-testid="navbar-search"
        >
            <form
                onSubmit={(e) => { e.preventDefault(); submit(); }}
                className="w-full"
            >
                <div className="nav-search-ring group rounded-full p-[1.5px] transition-shadow duration-300" data-testid="navbar-search-ring">
                <div className="flex items-center rounded-full bg-white/90 backdrop-blur-md pr-1 py-1">
                    <Search className={`w-4 h-4 ${iconMargin} text-slate-400 group-focus-within:text-[hsl(var(--primary))] transition-colors shrink-0`} />
                    <input
                        ref={inputRef}
                        value={q}
                        onChange={(e) => { setQ(e.target.value); setOpen(true); }}
                        onFocus={() => setOpen(true)}
                        type="text"
                        placeholder={t("home.quick_search_placeholder")}
                        className={`flex-1 min-w-0 px-3 ${inputPad} text-sm font-mono bg-transparent outline-none`}
                        data-testid="navbar-search-input"
                        autoComplete="off"
                    />
                    {q && (
                        <button
                            type="button"
                            onClick={() => { setQ(""); setSuggestions(null); inputRef.current?.focus(); }}
                            className="p-2 text-slate-400 hover:text-slate-900 shrink-0"
                            data-testid="navbar-search-clear"
                        >
                            <X className="w-4 h-4" />
                        </button>
                    )}
                    <button
                        type="submit"
                        className={`nav-search-btn relative overflow-hidden rounded-full text-white ${submitPad} font-display font-black uppercase tracking-wider shrink-0 flex items-center gap-1.5`}
                        data-testid="navbar-search-submit"
                    >
                        <span className="relative z-10">{t("common.search")}</span>
                        <ArrowRight className="relative z-10 w-3.5 h-3.5 transition-transform duration-300 group-hover:translate-x-0.5" />
                    </button>
                </div>
                </div>

                {/* Live suggestions dropdown */}
                {open && suggestions !== null && q.trim().length >= 2 && (
                    <div
                        className="absolute top-full left-0 right-0 mt-1 bg-white border-2 border-slate-900 shadow-[4px_4px_0_0_rgba(15,23,42,0.8)] z-[70] max-h-[420px] overflow-auto"
                        data-testid="navbar-search-suggestions"
                    >
                        {suggestions.length === 0 ? (
                            <div className="p-4 text-sm font-mono text-slate-500 text-center">
                                {t("home.no_matches")}
                            </div>
                        ) : (
                            <>
                                {suggestions.map((p) => (
                                    <Link
                                        key={p.id}
                                        to={`/product/${p.id}`}
                                        onClick={() => setOpen(false)}
                                        className="flex items-center gap-3 p-2 border-b border-slate-100 hover:bg-slate-50 transition-colors"
                                        data-testid={`navbar-search-suggestion-${p.id}`}
                                    >
                                        <div className="w-10 h-10 bg-slate-50 border border-slate-200 shrink-0 flex items-center justify-center">
                                            {p.image_url ? (
                                                <img src={p.image_url} alt="" className="w-full h-full object-contain p-0.5" onError={(e) => (e.currentTarget.style.display = "none")} />
                                            ) : (
                                                <Package className="w-5 h-5 text-slate-300" />
                                            )}
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <div className="font-display font-bold text-sm uppercase truncate">{p.name}</div>
                                            <div className="font-mono text-[11px] text-slate-500 truncate flex items-center gap-1.5">
                                                {p.category_name}
                                                {p.brand_name && <span className="px-1 bg-slate-900 text-white text-[9px]">{p.brand_name}</span>}
                                                {p.aftermarket_brand_name && <span className="px-1 bg-amber-100 text-amber-900 border border-amber-300 text-[9px]">{p.aftermarket_brand_name}</span>}
                                            </div>
                                        </div>
                                        <ArrowRight className="w-4 h-4 text-slate-400 shrink-0" />
                                    </Link>
                                ))}
                                <button
                                    type="button"
                                    onClick={() => submit()}
                                    className="w-full p-2.5 text-center font-display font-bold uppercase text-xs tracking-wider bg-slate-50 hover:bg-[hsl(var(--primary))] hover:text-white transition-colors"
                                    data-testid="navbar-search-view-all"
                                >
                                    {t("home.view_all_results")} "{q}" →
                                </button>
                            </>
                        )}
                    </div>
                )}
            </form>
        </div>
    );
}
